// Component: HoldingsTable.jsx
// Purpose: Presentational broker-holdings table — per-account degraded/stale/
//          no-holdings/unavailable states, human-symbol priority chain reused
//          from PositionsTable (10494-13 change 25), plus LTP/Value/P&L columns
//          with price-age greying and a bounded "last known" fallback block
//          (bundle 7A F4, docs/pipelines/2026-09-21-issue-bundle-7a-holdings-read-path/02-fix-design.md).

import React from 'react';
import { DataTable } from '../ds';

/*>>> CONSTANTS <<<*/

// Column count drives the colSpan of the account-level status rows below.
const COLUMNS = [
  { key: 'broker',       label: 'Broker' },
  { key: 'account',      label: 'Account' },
  { key: 'symbol',       label: 'Symbol' },
  { key: 'exchange',     label: 'Exchange' },
  { key: 'qty_settled',  label: 'Qty (settled)', align: 'right' },
  { key: 'qty_btst',     label: 'Qty (BTST/T1)', align: 'right' },
  { key: 'upload_price', label: 'Upload price (DP)', align: 'right', title: 'uploaded with the holding' },
  { key: 'ltp',          label: 'LTP', align: 'right' },
  { key: 'current_value', label: 'Value', align: 'right' },
  { key: 'pnl',          label: 'P&L', align: 'right' },
];

// --> Price-age threshold above which LTP/Value/P&L are greyed (F3/F4, D7 + AC-16R):
//   price:last has no TTL, so age is the only freshness signal. Unknown age (null) is
//   NEVER rendered fresh — treated the same as stale.
const PRICE_STALE_AGE_S = 900;

// --> IST time formatter for the broker snapshot "As of …" sub-label (mirrors
//   PositionsTable.jsx:40-56 — same locale/timezone/format, not re-derived).
const _asOfFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

// --> IST time formatter for the "price HH:MM" greyed-price sublabel (F4) — minute
//   precision only, distinct from the seconds-precision snapshot "As of" label above.
const _priceTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const formatAsOf = (asOf) => {
  if (!asOf) return null;
  try {
    return _asOfFmt.format(new Date(asOf));
  } catch {
    return null;
  }
};

// --> Numeric formatter for qty/upload_price/ltp/value cells (en-IN, 2 decimals for
//   money-shaped fields, plain for qty).
const formatQty = (val) => {
  if (val == null) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-IN');
};

const formatMoney = (val) => {
  if (val == null) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// --> P&L formatter — prefixes '+' for positive values (reuses PositionsTable.jsx:22-31,
//   same convention, not re-derived).
const formatPnl = (val) => {
  if (val == null) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  const formatted = Math.abs(num).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num >= 0 ? `+${formatted}` : `-${formatted}`;
};

const formatPnlPct = (val) => {
  if (val == null) return null;
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return null;
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
};

// --> Colour class for any P&L value (reuses PositionsTable.jsx:34-37).
const pnlColor = (val) => {
  if (val == null || isNaN(val)) return 'text-fg-2';
  return val >= 0 ? 'text-profit' : 'text-loss';
};

// --> D7/AC-16R: unknown age is NEVER rendered fresh — null counts as stale.
const isPriceStale = (row) => row.ltp_age_s == null || row.ltp_age_s > PRICE_STALE_AGE_S;

// --> "price HH:MM" sublabel derived from age-at-response (no ltp_ts on the row —
//   only ltp_age_s is on the wire); falls back to a dash when age itself is unknown.
const priceAgeLabel = (row) => {
  if (row.ltp_age_s == null) return 'price —';
  try {
    const ts = Date.now() - row.ltp_age_s * 1000;
    return `price ${_priceTimeFmt.format(new Date(ts))}`;
  } catch {
    return 'price —';
  }
};

const minutesAgo = (ageS) => {
  if (ageS == null) return null;
  return Math.max(0, Math.round(ageS / 60));
};

// --> D2/instrument-id-display-remediation S3 priority chain, reused from
//   PositionsTable.jsx:288-298 — visible text NEVER shows a raw UUID.
//   Priority: display_symbol → symbol → instrument_symbol → name → broker_symbol → '—'
//! `broker_symbol` is APPENDED as the last resort (eng-tech-lead integration fix): the
//  FT adapter's normalised holdings row (change 17) emits the human symbol under
//  `broker_symbol` (`exch_tsym[0].tsym`, e.g. "IDEA-EQ") and emits NO `display_symbol`/
//  `symbol`/`instrument_symbol`/`name` key at all, so the verbatim PositionsTable chain
//  alone would render '—' for EVERY holdings row — a hollow-green Symbol column. The
//  design's own Data-lineage row names this datum "Symbol (human) ← exch_tsym[0].tsym",
//  so the value is right and only the field name differed between change 17 and change
//  25. `broker_symbol` is a vendor trading symbol, NEVER an id — the never-show-a-raw-id
//  invariant is unaffected.
const symbolLabel = (row) =>
  row.display_symbol ?? row.symbol ?? row.instrument_symbol ?? row.name ?? row.broker_symbol ?? '—';

// --> Account column: "<Broker> · <broker_client_id>", falling back to "<Broker>" alone.
//   Never a raw account_id UUID on a human surface (D23/R16b).
const accountLabel = (account) => {
  const broker = account.broker || '—';
  return account.broker_client_id ? `${broker} · ${account.broker_client_id}` : broker;
};

/*>>> SUB-COMPONENT: AccountStatusRow — spans every column, used for the degraded/
      no-holdings/unavailable per-account states (never a bare omitted account) <<<*/
const AccountStatusRow = ({ account, message, reason }) => (
  <DataTable.Row>
    <DataTable.Cell colSpan={COLUMNS.length} muted>
      <span className="text-sm text-fg-2" title={reason || undefined}>
        {message}
      </span>
    </DataTable.Cell>
  </DataTable.Row>
);

// --> Sub-row: "stale · as of HH:MM:SS" chip on the account block (F2/F4) — the served
//   rows are a bounded read-through-cache serve (RATE_LIMITED ≤ 15 min), not a failure.
const StaleChipRow = ({ account }) => {
  const asOfLabel = formatAsOf(account.as_of);
  return (
    <DataTable.Row>
      <DataTable.Cell colSpan={COLUMNS.length}>
        <span className="text-[10px] text-fg-2 tabular-nums" data-testid="holdings-stale-chip">
          stale{asOfLabel ? ` · as of ${asOfLabel}` : ''}
        </span>
      </DataTable.Cell>
    </DataTable.Row>
  );
};

// --> Sub-row: "last known · N min ago" label preceding the greyed last-known rows
//   block (F2/F4) — rendered only when a degraded account still has a bounded (≤4h)
//   snapshot to show.
const LastKnownLabelRow = ({ lastKnown }) => {
  const mins = minutesAgo(lastKnown.age_s);
  return (
    <DataTable.Row>
      <DataTable.Cell colSpan={COLUMNS.length} muted>
        <span className="text-[10px] text-fg-2 tabular-nums" data-testid="holdings-last-known-label">
          last known{mins != null ? ` · ${mins} min ago` : ''}
        </span>
      </DataTable.Cell>
    </DataTable.Row>
  );
};

// --> Shared row renderer for both live/stale-served rows and greyed last-known rows.
//   `greyed` mutes every cell (DataTable.Cell `muted` prop) for the last-known block.
const HoldingRow = ({ account, row, greyed = false }) => {
  const priceStale = isPriceStale(row);
  const priceClass = priceStale || greyed ? 'text-fg-2' : undefined;
  const pnlNum = row.pnl == null ? null : typeof row.pnl === 'number' ? row.pnl : parseFloat(row.pnl);
  const pnlPctLabel = formatPnlPct(row.pnl_pct);

  return (
    <DataTable.Row key={`${account.account_id}-${row.instrument_id}`}>
      <DataTable.Cell muted={greyed}>{account.broker || '—'}</DataTable.Cell>
      <DataTable.Cell muted={greyed} title={account.account_id || undefined}>
        {accountLabel(account)}
      </DataTable.Cell>
      <DataTable.Cell muted={greyed}>
        <span title={[symbolLabel(row), row.exchange].filter(Boolean).join(' · ')}>
          {symbolLabel(row)}
        </span>
      </DataTable.Cell>
      <DataTable.Cell muted={greyed}>{row.exchange || '—'}</DataTable.Cell>
      <DataTable.Cell align="right" muted={greyed}>
        <span className="font-mono text-sm tabular-nums">{formatQty(row.qty_settled)}</span>
      </DataTable.Cell>
      <DataTable.Cell align="right" muted={greyed}>
        <span className="font-mono text-sm tabular-nums">{formatQty(row.qty_btst)}</span>
      </DataTable.Cell>
      <DataTable.Cell align="right" muted={greyed} title="uploaded with the holding">
        <span className="font-mono text-sm tabular-nums">{formatMoney(row.upload_price)}</span>
      </DataTable.Cell>
      {/* --> LTP / Value / P&L (F3/F4) — greyed + "price HH:MM" when ltp_age_s > 900
            or null (unknown age is never rendered fresh); '—' when ltp itself is null. */}
      <DataTable.Cell align="right" muted={greyed}>
        <div className="flex flex-col items-end">
          <span className={`font-mono text-sm tabular-nums ${priceClass || ''}`}>
            {formatMoney(row.ltp)}
          </span>
          {row.ltp != null && (priceStale || greyed) && (
            <span className="text-[10px] text-fg-2" data-testid="holdings-price-age">
              {priceAgeLabel(row)}
            </span>
          )}
        </div>
      </DataTable.Cell>
      <DataTable.Cell align="right" muted={greyed}>
        <span className={`font-mono text-sm tabular-nums ${priceClass || ''}`}>
          {formatMoney(row.current_value)}
        </span>
      </DataTable.Cell>
      <DataTable.Cell align="right" muted={greyed}>
        <span
          className={`font-mono text-sm tabular-nums ${greyed || priceStale ? 'text-fg-2' : pnlColor(pnlNum)}`}
        >
          {formatPnl(row.pnl)}
          {pnlPctLabel && <span className="text-xs"> ({pnlPctLabel})</span>}
        </span>
      </DataTable.Cell>
    </DataTable.Row>
  );
};

/*>>> MAIN COMPONENT <<<*/

/**
 * HoldingsTable — broker-holdings tab (10494-13 change 25 + bundle 7A F4).
 *
 * Purely presentational: fed entirely by the parent (Orders.jsx). No fetching, no
 * context writes here.
 *
 * @param {Object[]} holdings - Array of holding rows (contract: instrument_id, broker_symbol,
 *                              symbol|display_symbol|instrument_symbol|name, exchange,
 *                              qty_settled, qty_btst, upload_price, product, account_id, broker,
 *                              broker_client_id, ltp, ltp_age_s, current_value, pnl, pnl_pct).
 * @param {Object[]} accounts - Array of per-account fan-out results (account_id, broker,
 *                              broker_client_id, degraded, error_code, error, holdings_complete,
 *                              stale, as_of, last_known:{as_of, age_s, rows}).
 * @param {string}   asOf     - Broker snapshot as_of ISO timestamp (top-level, response.data.as_of).
 */
const HoldingsTable = ({ holdings = [], accounts = [], asOf }) => {
  const asOfLabel = formatAsOf(asOf);

  return (
    <div className="flex flex-col gap-3">
      {/*>>> TAB-LEVEL HEADER + BANNER — release-scope statement, NOT a capability registry <<<*/}
      {/* DELETED by 10494-13-FU1 (see ## Decommission) — do not generalize into a
          broker-support table anywhere. */}
      <h3 className="text-sm font-semibold text-fg-1" data-testid="holdings-header">
        Broker holdings (live accounts only)
      </h3>
      <p className="text-xs text-fg-2" data-testid="holdings-scope-banner">
        Holdings are read from FlatTrade accounts only in this release; ICICI holdings are
        not shown yet.
      </p>

      <div className="overflow-hidden rounded-xl border border-[var(--divider)] bg-bg-2">
        {asOfLabel && (
          <div className="border-b border-[var(--divider)] px-4 py-2">
            <span className="text-[10px] text-fg-2 tabular-nums" title={`Broker snapshot as of ${asOfLabel}`}>
              As of {asOfLabel} (broker snapshot)
            </span>
          </div>
        )}
        <div className="overflow-x-auto">
          <DataTable className="w-full">
            <DataTable.Head>
              <DataTable.Row>
                {COLUMNS.map((col) => (
                  <DataTable.HeaderCell key={col.key} align={col.align || 'left'} title={col.title || undefined}>
                    {col.label}
                  </DataTable.HeaderCell>
                ))}
              </DataTable.Row>
            </DataTable.Head>
            <DataTable.Body>
              {accounts.map((account) => {
                const rows = holdings.filter((h) => h.account_id === account.account_id);

                //! D25/R3-5 — the `=== true` comparisons here are load-bearing. An absent
                //  key is falsy in JS; a truthiness test would silently take the
                //  "No holdings" branch and render a hollow-green empty state for a book
                //  we never actually fetched. Do not "simplify" these to truthiness.

                // 1) degraded === true -> reason text, plus (F2/F4) a bounded greyed
                //    "last known" rows block when a snapshot ≤4h old exists. The literal
                //    "Holdings unavailable for <broker>" wording is used ONLY when there
                //    is no last_known to fall back to (v).
                if (account.degraded === true) {
                  const lastKnown = account.last_known;
                  const message = lastKnown
                    ? account.error || `Holdings for ${accountLabel(account)} temporarily unavailable`
                    : `Holdings unavailable for ${accountLabel(account)}`;

                  const elements = [
                    <AccountStatusRow
                      key={`${account.account_id}-status`}
                      account={account}
                      message={message}
                      reason={account.error}
                    />,
                  ];

                  if (lastKnown) {
                    elements.push(
                      <LastKnownLabelRow key={`${account.account_id}-lastknown-label`} lastKnown={lastKnown} />
                    );
                    (lastKnown.rows || []).forEach((row) => {
                      elements.push(
                        <HoldingRow
                          key={`${account.account_id}-lastknown-${row.instrument_id}`}
                          account={account}
                          row={row}
                          greyed
                        />
                      );
                    });
                  }

                  return elements;
                }

                // 2) not degraded AND holdings_complete === true AND zero rows ->
                //    broker-attested empty book. A bounded stale serve (RATE_LIMITED
                //    ≤15 min) still satisfies holdings_complete (F2) — the stale chip
                //    carries the distinction.
                if (account.holdings_complete === true) {
                  const staleChip = account.stale === true
                    ? [<StaleChipRow key={`${account.account_id}-stale`} account={account} />]
                    : [];

                  if (rows.length === 0) {
                    return [
                      ...staleChip,
                      <AccountStatusRow
                        key={`${account.account_id}-no-holdings`}
                        account={account}
                        message={`No holdings for ${accountLabel(account)}`}
                      />,
                    ];
                  }
                  // Happy path: broker confirmed a complete book (live or bounded-stale)
                  // AND it has rows — render the real holding rows for this account.
                  return [
                    ...staleChip,
                    ...rows.map((row) => (
                      <HoldingRow
                        key={`${account.account_id}-${row.instrument_id}`}
                        account={account}
                        row={row}
                      />
                    )),
                  ];
                }

                // 3) anything else — holdings_complete false, undefined, or absent ->
                //    treated as unavailable, same render as (1), with its own reason.
                return (
                  <AccountStatusRow
                    key={account.account_id}
                    account={account}
                    message={`Holdings unavailable for ${accountLabel(account)}`}
                    reason="broker did not confirm a complete book"
                  />
                );
              })}
            </DataTable.Body>
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default HoldingsTable;
